import { useState, type ReactNode } from 'react';
import { ImageCropDialog } from '@/components/common/ImageCropDialog';
import { CROPPABLE_MIME_TYPES, IMAGE_CROP_SPECS } from '@/lib/constants/image-crop';
import type { UploadKind } from '@/api/uploads.api';

export interface ImageCropFlow {
  /**
   * Call with the file the member picked. A PNG or JPEG for a croppable kind opens
   * the crop dialog, and `onReady` runs with the cropped file when they confirm;
   * anything else (a PDF, a kind with no crop frame, a type the control's own check or
   * the server policy will refuse) goes to `onReady` untouched.
   */
  requestFile: (file: File) => void;
  /** Render this once next to the control; it is empty until a crop is pending. */
  cropDialog: ReactNode;
}

/**
 * The shared crop step in front of every image upload (FR-121). Each upload control
 * calls `requestFile` where it used to start the upload, and renders `cropDialog`.
 * Cancelling the dialog calls nothing, so the previous image is kept and nothing is
 * uploaded.
 *
 * @param kind - the upload kind, which picks the locked frame from `IMAGE_CROP_SPECS`
 * @param onReady - starts the control's existing upload with the final file
 */
export function useImageCrop(kind: UploadKind, onReady: (file: File) => void): ImageCropFlow {
  const [pending, setPending] = useState<File | null>(null);
  const spec = IMAGE_CROP_SPECS[kind];

  const requestFile = (file: File): void => {
    if (spec && CROPPABLE_MIME_TYPES.includes(file.type)) setPending(file);
    else onReady(file);
  };

  const cropDialog =
    pending && spec ? (
      <ImageCropDialog
        file={pending}
        spec={spec}
        onCancel={() => setPending(null)}
        onConfirm={(cropped) => {
          setPending(null);
          onReady(cropped);
        }}
      />
    ) : null;

  return { requestFile, cropDialog };
}
